import type { FC, PropsWithChildren } from "react";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { QuestionCard } from "../types";
import { InfoIcon } from "../../shared/icons";
import { InfoPopover } from "../inlineForm/InfoPopover";
import { useHoverPopover } from "../../shared/HoverPopover";

const PADDING_Y = 4;
const PADDING_Y_SMALL = 3;
const PADDING_X = 5;
const PADDING_X_SMALL = 4;
const BREAKPOINT = "750px";
const SMALLER_THAN_BREAKPOINT_QUERY = `@container (max-width: ${BREAKPOINT})`;

export const QuestionCardContent: FC<PropsWithChildren<{ questionCard: QuestionCard }>> = ({
  children,
  questionCard,
}) => {
  const { header, questionText, description, helpInformation, extraHelpInformation, id } = questionCard;

  return (
    <Card
      sx={{
        containerType: "inline-size",
        mb: 3,
        borderRadius: 1,
        maxWidth: "1080px",
        minHeight: "300px",
        boxShadow: "none",
        border: "1px solid",
        borderColor: theme => theme.palette.grey["400"],
      }}
      aria-labelledby={id}
    >
      <Stack
        direction="row"
        sx={{
          flexWrap: "wrap",
          gap: 6,
          justifyContent: "space-between",
          px: PADDING_X,
          py: PADDING_Y,
          [SMALLER_THAN_BREAKPOINT_QUERY]: { px: PADDING_X_SMALL, py: PADDING_Y_SMALL },
        }}
      >
        <Box sx={{ flex: "1", minWidth: "200px", [SMALLER_THAN_BREAKPOINT_QUERY]: { minWidth: "100%" } }}>
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <Typography
              sx={{
                fontSize: theme => theme.typography.pxToRem(12),
                textTransform: "uppercase",
                fontWeight: "700",
                letterSpacing: "0.5px",
                color: theme => theme.palette.primary.main,
              }}
            >
              {header}
            </Typography>
            <Box sx={{ display: "none", [SMALLER_THAN_BREAKPOINT_QUERY]: { display: "block" } }}>
              <InfoPopoverIcon body={helpInformation} extraHelpInformation={extraHelpInformation} />
            </Box>
          </Stack>
          <Box sx={{ height: 12 }} />
          <Typography variant="h3" id={id}>
            {questionText}
          </Typography>

          <Typography
            sx={{ textWrap: "balance", whiteSpace: "pre-wrap", color: theme => theme.palette.text.tertiary }}
            variant="body2"
          >
            {description}
          </Typography>

          <Box sx={{ pt: 4 }}>{children}</Box>
        </Box>

        {helpInformation && (
          <Box sx={{ pl: 2, width: "280px", py: 1, [SMALLER_THAN_BREAKPOINT_QUERY]: { display: "none" } }}>
            <Stack spacing={2}>
              <Typography
                sx={{ textWrap: "balance", whiteSpace: "pre-wrap", color: theme => theme.palette.text.tertiary }}
                variant="body2"
              >
                {helpInformation}
              </Typography>
              {extraHelpInformation && (
                <Stack
                  direction="row"
                  sx={{
                    border: "1px",
                    borderColor: theme => theme.palette.grey["200"],
                    bgcolor: theme => theme.palette.grey["100"],
                    alignItems: "center",
                    padding: 3,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: theme => theme.typography.pxToRem(12),
                      textWrap: "balance",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {extraHelpInformation}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Card>
  );
};

const InfoPopoverIcon: FC<{ body?: string; extraHelpInformation?: string }> = ({ body, extraHelpInformation }) => {
  const { popoverProps, anchorProps } = useHoverPopover();
  return (
    <>
      <Box
        component="span"
        {...anchorProps}
        sx={{
          p: 1,
          transform: "scale(4)",
        }}
      >
        <InfoIcon size={12} strokeWidth={2.5} color={theme => theme.palette.primary.main} />
      </Box>
      <InfoPopover body={body} info={extraHelpInformation} {...popoverProps} />
    </>
  );
};
